/**
 * Indexed Hero — Full Page
 * Dark Tech / Glassmorphism design
 * Colors: #0c0c0c bg, #57d7e2 cyan accent
 * Font: Barlow
 */

import React, { useState, useEffect, useRef } from "react";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useIsMobile } from "@/hooks/useIsMobile";
import SiteLayout from "@/components/SiteLayout";
import HeroFloatingTiles from "@/components/HeroFloatingTiles";
import ServiceLadder from "@/components/ServiceLadder";
import GscProof from "@/components/GscProof";
import VisibilityCheckerModule from "@/components/VisibilityCheckerModule";
import CaseStudyProof from "@/components/CaseStudyProof";

// Eager-imported to match /revenue-driven-seo's single-bundle loading pattern.
// On mobile, one bigger initial download beats multiple async chunk fetches —
// fewer round-trips, no Suspense fallback flashes, snappier perceived load.
import HomeBlogSection from "@/components/HomeBlogSection";
import TestimonialWall from "@/components/TestimonialWall";
import { VIDEO_REVIEW_URL as STRATEGIST_URL } from "@/lib/cta";

const CDN = "https://d2xsxph8kpxj0f.cloudfront.net/310519663444343231/KfbWTnTA9XstaA7FAytXMj";
const ff = "'Barlow', sans-serif";

const LOGO_ASSETS = [
  `/4941-9030.webp`, `/4941-9031.webp`,
  `/4941-9032.webp`, `/4941-9033.webp`,
  `/4941-9034.webp`, `/4941-9035.webp`,
  `/4941-9036.webp`, `/4941-9037.webp`,
  `/4941-9038.webp`, `/4941-9039.webp`,
  `/4941-9040.webp`, `/4941-9041.webp`,
  `/4941-9042.webp`, `/4941-9043.webp`,
  `/4941-9044.webp`, `/4941-9045.webp`,
  `/4941-9046.webp`, `/4941-9047.webp`,
  `/4941-9048.webp`, `/4941-9049.webp`,
  `/4941-9050.webp`, `/4941-9051.webp`,
  `/4941-9052.webp`, `/4941-9053.webp`,
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663444343231/KfbWTnTA9XstaA7FAytXMj/smartfog_87dd78bd.webp",

];

// ── Small icons ────────────────────────────────────────────────────────
const ArrowUpRight = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none" style={{ display: "inline-block" }}>
    <path d="M3 11L11 3M11 3H5M11 3v6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SEO_SERVICES = [
  { label: "SEO Audit & Strategy", desc: "Get direction for growth.", href: "/seo-audit-and-strategy" },
  { label: "AI SEO (AEO & GEO)", desc: "Get cited in ChatGPT & Gemini.", href: "/ai-seo" },
  { label: "Technical SEO", desc: "Strengthen site health.", href: "/technical-seo" },
  { label: "Local SEO", desc: "Win local visibility.", href: "/local-seo" },
  { label: "Content Production", desc: "Publish content that ranks.", href: "/content-production" },
  { label: "Programmatic SEO", desc: "Capture long-tail demand at scale.", href: "/programmatic-seo" },
  { label: "Link Building", desc: "Increase authority with backlinks.", href: "/link-building" },
];

// ── Service item in dropdown ───────────────────────────────────────────
function ServiceItem({ label, desc, href, onClose }: { label: string; desc: string; href: string; onClose?: () => void }) {
  const [hov, setHov] = useState(false);
  return (
    <a
      href={href}
      onClick={() => onClose?.()}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex", alignItems: "flex-start", gap: "10px",
        padding: "9px 12px", borderRadius: "8px", textDecoration: "none",
        background: hov ? "rgba(87,215,226,0.07)" : "transparent",
        transition: "background 0.15s",
      }}
    >
      <div>
        <div style={{ color: "white", fontSize: "14px", fontWeight: 600, fontFamily: ff }}>{label}</div>
        <div style={{ color: "rgba(255,255,255,0.42)", fontSize: "12px", fontFamily: ff, marginTop: "2px" }}>{desc}</div>
      </div>
    </a>
  );
}

// ── Compact FAQ item (matches service page style) ─────────────────────────
function HomeFaqItem({ faq, index, isLast }: { faq: { q: string; a: string }; index: number; isLast: boolean }) {
  const [open, setOpen] = React.useState(false);
  const ff = "'Barlow', sans-serif";
  return (
    <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", borderBottom: isLast ? "1px solid rgba(255,255,255,0.08)" : "none" }}>
      <button type="button"
        onClick={() => setOpen(!open)}
        style={{ width: "100%", padding: "24px 0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}
      >
        <span style={{ fontSize: "17px", fontWeight: 600, color: "white", fontFamily: ff, lineHeight: 1.4 }}>{faq.q}</span>
        <span style={{ color: "#57d7e2", fontSize: "24px", fontWeight: 300, flexShrink: 0, transform: open ? "rotate(45deg)" : "none", transition: "transform 0.2s", lineHeight: 1 }}>+</span>
      </button>
      {open && (
        <div style={{ paddingBottom: "24px" }}>
          <p style={{ margin: 0, fontSize: "15px", color: "rgba(255,255,255,0.6)", fontFamily: ff, lineHeight: 1.8 }}>{faq.a}</p>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────

function FaqSection() {
  const ff = "'Barlow', sans-serif";
  const isMobile = useIsMobile();
  const CDN = "https://d2xsxph8kpxj0f.cloudfront.net/310519663444343231/KfbWTnTA9XstaA7FAytXMj";
  const faqs = [
    {
      q: "How do I know what my site is losing in revenue?",
      a: "That's exactly what our free video review is designed to answer. In a 10–15 minute video recorded for you, we identify the specific issues costing you the most traffic and revenue — and give you a clear picture of what fixing them is worth. No pitch, no obligation.",
    },
    {
      q: "Do you sell standalone SEO services or packages?",
      a: "We don't sell one-off audits or isolated tactics. Everything we do is part of the Indexed Full-Stack Search Method™ — a complete, end-to-end engagement covering technical SEO, content, links, tracking, and design. This is why our clients see 3.5× ROI on average.",
    },
    {
      q: "We've had SEO agencies before and seen no results. Why is Indexed different?",
      a: "Most agencies deliver reports. We deliver revenue. Every engagement is tracked against pipeline and closed revenue — not just rankings or traffic. We also implement fixes directly rather than handing you a document and walking away.",
    },
    {
      q: "How long before we see results?",
      a: "Most clients see measurable improvements in organic traffic within 3–4 months. Revenue impact typically follows at 6–8 months as rankings compound. Our solar client hit $1.1m in revenue within 10 months.",
    },
    {
      q: "Do you work with businesses outside of B2B SaaS?",
      a: "Yes — we work across sectors including renewable energy, professional services, fintech, biotech, storage, and e-commerce. The Full-Stack Search Method™ is designed to drive revenue regardless of industry.",
    },
    {
      q: "How many new clients do you take on?",
      a: "We take on 3–4 new clients per month to maintain quality and senior attention on every engagement. If you're considering working with us, booking a session early is the best way to check availability.",
    },
  ];
  const [openIdx, setOpenIdx] = React.useState(0);

  return (
    <section className="section-grid" style={{ background: "#0c0c0c", padding: isMobile ? "60px 20px" : "100px 72px", position: "relative", overflow: "hidden" }}>

      {/* Cyan glow */}
      <div style={{ position: "absolute", left: "50%", top: "150px", transform: "translateX(-50%) rotate(90deg)", width: "381px", height: "118px", background: "#3aefff", opacity: 0.5, filter: "blur(175px)", pointerEvents: "none" }} />

      <div style={{ maxWidth: "1070px", margin: "0 auto", position: "relative", display: "flex", flexDirection: "column", alignItems: "center", gap: "72px" }}>
        <div role="heading" aria-level={2} style={{ color: "white", fontSize: isMobile ? "32px" : "48px", fontWeight: 700, fontFamily: ff, margin: 0, textAlign: "center", lineHeight: 1.1 }}>
          What to know <br/><span style={{ color: "#57d7e2" }}>before getting started.</span>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "20px", width: "100%" }}>
          {faqs.map((faq, i) => (
            <div
              key={faq.q}
              onClick={() => setOpenIdx(openIdx === i ? -1 : i)}
              style={{
                padding: openIdx === i ? "40px" : "30px 40px",
                background: "rgba(255,255,255,0)",
                borderRadius: "20px",
                border: openIdx === i ? "1px solid #57d7e2" : "1px solid rgba(255,255,255,0.10)",
                backdropFilter: "blur(35px)",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                gap: openIdx === i ? "20px" : "0",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "40px" }}>
                <span style={{ color: "white", fontSize: "32px", fontWeight: 500, fontFamily: ff, flex: 1, lineHeight: 1.3 }}>{faq.q}</span>
                <span style={{ color: "white", fontSize: "32px", fontWeight: 400, fontFamily: ff, flexShrink: 0 }}>{openIdx === i ? "−" : "+"}</span>
              </div>
              {openIdx === i && (
                <p style={{ color: "#b4babb", fontSize: "20px", fontWeight: 400, fontFamily: ff, margin: 0, lineHeight: 1.6 }}>{faq.a}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


export default function Home() {
  useSeoMeta({
    title: "Indexed | Full-Stack SEO, AEO & GEO Agency",
    description: "Full-stack SEO, AEO and GEO agency helping you get found on Google, ChatGPT and AI Overviews so your visibility turns into compounding revenue.",
    canonical: "/",
    ogType: "website",
    jsonLd: [
    {
      "@type": "Organization",
      "@id": "https://www.joinindexed.com/#organization",
      "name": "Indexed",
      "url": "https://www.joinindexed.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.joinindexed.com/images/og-default.png",
        "width": 1200,
        "height": 630,
      },
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Meydan Grandstand, Meydan Road, Nad Al Sheba",
        "addressLocality": "Dubai",
        "addressCountry": "AE",
      },
      "sameAs": [
        "https://share.google/AvRKwCYP0CI2KeICu",
        "https://www.linkedin.com/company/indexedseo",
        "https://x.com/Indexed_Agency",
        "https://www.instagram.com/indexedagency",
        "https://www.facebook.com/indexedseoagency"
      ],
      "description": "Indexed is a full-stack SEO, AEO and GEO agency that gets brands seen everywhere their buyers search — Google, ChatGPT, Gemini and AI Overviews — through seven integrated pillars.",
      "foundingDate": "2024",
      "knowsAbout": ["SEO", "AI SEO", "AEO", "Answer Engine Optimization", "GEO", "Generative Engine Optimization", "AI Search Visibility", "Content SEO", "Link Building", "Technical SEO", "Local SEO", "Programmatic SEO"],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "SEO Services",
        "itemListElement": [
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Technical SEO" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Content Production" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Link Building" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "AI SEO & GEO" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Local SEO" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Programmatic SEO" } },
        ],
      },
    },
    {
      "@type": "WebSite",
      "@id": "https://www.joinindexed.com/#website",
      "name": "Indexed",
      "url": "https://www.joinindexed.com",
      "publisher": { "@id": "https://www.joinindexed.com/#organization" },
      "inLanguage": "en-US",
      "potentialAction": {
        "@type": "SearchAction",
        "target": { "@type": "EntryPoint", "urlTemplate": "https://www.joinindexed.com/blog?q={search_term_string}" },
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@type": "WebPage",
      "@id": "https://www.joinindexed.com/#webpage",
      "url": "https://www.joinindexed.com",
      "name": "Indexed | Full-Stack SEO, AEO & GEO Agency",
      "isPartOf": { "@id": "https://www.joinindexed.com/#website" },
      "about": { "@id": "https://www.joinindexed.com/#organization" },
      "description": "We get you seen everywhere your buyers search — Google, ChatGPT, Gemini & AI Overviews. Full-stack SEO, AEO & GEO that turns visibility into compounding revenue.",
    },
  ],
  });
  const isMobile = useIsMobile();
  const [scrolled, setScrolled] = useState(false);
  const [hovSvcCard, setHovSvcCard] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onScroll = () => {
      const nearBottom = (window.innerHeight + window.scrollY) >= document.documentElement.scrollHeight - 80;
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);


  return (
    <SiteLayout>
      {/* ── HERO SECTION ── */}
      <HeroFloatingTiles />

      {/* ── LOGO MARQUEE ── */}
      <section style={{ background: "#080a0a" }}>
        {/* Context line — quantifies the logos */}
        <div style={{ textAlign: "center", padding: "28px 20px 20px" }}>
          <span style={{ color: "rgba(255,255,255,0.45)", fontSize: isMobile ? "12px" : "13px", fontWeight: 600, fontFamily: ff, letterSpacing: "0.12em", textTransform: "uppercase" as const }}>
            Trusted by 100+ teams across the US, UK &amp; Europe
          </span>
        </div>
        {/* Logo marquee */}
        <div style={{ position: "relative", borderTop: "1px solid rgba(255,255,255,0.08)", padding: "16px 0", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right, #000202 0%, transparent 12%, transparent 88%, #000202 100%)", zIndex: 2, pointerEvents: "none" }} />
          <div style={{ display: "flex", alignItems: "center", gap: "50px", animation: "scroll-logos 50s linear infinite", width: "max-content" }}>
            {[...LOGO_ASSETS, ...LOGO_ASSETS].map((src, idx) => (
              <img loading="lazy" key={idx} src={src} alt={`client logo ${idx}`} style={{ width: "135px", height: "90px", objectFit: "contain", flexShrink: 0, filter: "grayscale(1) brightness(2)" }} />
            ))}
          </div>
        </div>
      </section>

      {/* ── AI VISIBILITY CHECKER — the low-commitment way in, high on the page ── */}
      <VisibilityCheckerModule />

      {/* ── PROOF — real Search Console exports, click to expand ── */}
      <GscProof />

      {/* ── TESTIMONIALS — the client voices, sat between the two proof modules.
           The Search Console curves show the change, the people it happened to
           vouch for it, then the revenue numbers say what it was worth.

           Plain, no .section-grid: both proof modules either side are gridded,
           and three gridded panels in a row read as one block with no boundary. ── */}
      <section id="testimonials" style={{ background: "#0a0c0c", padding: isMobile ? "60px 20px" : "100px 72px", scrollMarginTop: "80px" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <h2 style={{ color: "white", fontSize: isMobile ? "32px" : "48px", fontWeight: 700, fontFamily: ff, margin: "0 0 56px", lineHeight: 1.1, letterSpacing: "-0.02em", textAlign: "center" as const }}>
            What clients say<br /><span style={{ color: "#57d7e2" }}>about working with us.</span>
          </h2>
          <TestimonialWall isMobile={isMobile} />
        </div>
      </section>

      {/* ── PROOF, PART TWO — what those curves were worth, from brandData ── */}
      <CaseStudyProof />

      {/* ── FULL-STACK SEARCH METHOD — split: narrative left, upward pyramid right ── */}
      <section className="section-grid" style={{ backgroundColor: "#0a0c0c", padding: isMobile ? "60px 20px 40px" : "100px 72px 80px", overflow: "hidden" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1.1fr", gap: isMobile ? "48px" : "88px", alignItems: "center" }}>
          {/* Left — narrative */}
          <div>
            <h2 style={{ color: "white", fontSize: isMobile ? "32px" : "44px", fontWeight: 700, fontFamily: ff, margin: "0 0 24px", lineHeight: 1.12, letterSpacing: "-0.02em", textAlign: "left" as const }}>
              The Indexed Full-Stack<br /><span style={{ color: "#57d7e2" }}>Search Method™</span>
            </h2>
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: isMobile ? "16px" : "17px", fontFamily: ff, lineHeight: 1.75, margin: "0 0 16px" }}>
              Most agencies sell you pieces — an audit here, some content there. The pieces don't compound, so results stall.
            </p>
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: isMobile ? "16px" : "17px", fontFamily: ff, lineHeight: 1.75, margin: "0 0 32px" }}>
              We run all seven pillars as one system. The same signals that win Google rankings win AI answers — and when everything works together, visibility turns into pipeline and revenue that compounds.
            </p>
            <a
              href="/method"
              style={{
                display: "inline-flex", alignItems: "center",
                background: "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)",
                border: "1px solid #57d7e2", borderRadius: "88px", padding: "14px 28px",
                boxShadow: "0px 2px 15px 0px rgba(87,215,226,0.20)",
                color: "#121212", fontSize: "15px", fontWeight: 600, fontFamily: ff,
                textDecoration: "none", cursor: "pointer", transition: "opacity 0.2s",
              }}
              onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.opacity = "0.85"}
              onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.opacity = "1"}
            >
              See the full methodology
            </a>
          </div>

          {/* Right — upward pyramid: 7 Pillars (bottom/wide) → Visibility → Revenue (top/narrow) */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>

            {/* Tier 1 — Revenue (top / narrowest) */}
            <div style={{
              width: isMobile ? "70%" : "58%",
              background: "linear-gradient(180deg, rgba(87,215,226,0.22) 0%, rgba(87,215,226,0.10) 100%)",
              border: "1px solid rgba(87,215,226,0.55)",
              borderRadius: "12px",
              padding: isMobile ? "18px 16px" : "22px 24px",
              textAlign: "center" as const,
              boxSizing: "border-box" as const,
            }}>
              <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "radial-gradient(circle, #7af0ff 0%, #20b8c8 60%, #0e7a88 100%)", boxShadow: "0 0 18px 6px rgba(87,215,226,0.4)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
                <span style={{ color: "white", fontSize: "14px", fontWeight: 800, fontFamily: ff }}>$</span>
              </div>
              <div style={{ color: "white", fontSize: isMobile ? "15px" : "17px", fontWeight: 700, fontFamily: ff, lineHeight: 1.2, marginBottom: "5px" }}>Revenue</div>
              <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "13px", fontFamily: ff, lineHeight: 1.5 }}>Pipeline that compounds over time</div>
              <div style={{ marginTop: "12px" }}>
                <div style={{ color: "#57d7e2", fontSize: "20px", fontWeight: 700, fontFamily: ff, lineHeight: 1 }}>3.5×</div>
                <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "12px", fontFamily: ff, marginTop: "4px" }}>Avg ROI on SEO</div>
              </div>
            </div>

            {/* Upward arrow */}
            <div style={{ display: "flex", justifyContent: "center", height: "28px", alignItems: "center" }}>
              <svg width="20" height="28" viewBox="0 0 20 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="10" y1="28" x2="10" y2="8" stroke="rgba(87,215,226,0.4)" strokeWidth="1.5" strokeDasharray="3 3"/>
                <path d="M4 10L10 2L16 10" stroke="rgba(87,215,226,0.6)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            </div>

            {/* Tier 2 — Visibility (middle width) */}
            <div style={{
              width: isMobile ? "85%" : "78%",
              background: "linear-gradient(180deg, rgba(87,215,226,0.13) 0%, rgba(87,215,226,0.05) 100%)",
              border: "1px solid rgba(87,215,226,0.32)",
              borderRadius: "12px",
              padding: isMobile ? "18px 16px" : "20px 24px",
              textAlign: "center" as const,
              boxSizing: "border-box" as const,
            }}>
              <div style={{ color: "white", fontSize: isMobile ? "15px" : "17px", fontWeight: 700, fontFamily: ff, lineHeight: 1.2, marginBottom: "5px" }}>Visibility Across Google &amp; LLMs</div>
              <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "13px", fontFamily: ff, lineHeight: 1.5 }}>Search engines and AI models reward the same underlying signals</div>
            </div>

            {/* Upward arrow */}
            <div style={{ display: "flex", justifyContent: "center", height: "28px", alignItems: "center" }}>
              <svg width="20" height="28" viewBox="0 0 20 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <line x1="10" y1="28" x2="10" y2="8" stroke="rgba(87,215,226,0.4)" strokeWidth="1.5" strokeDasharray="3 3"/>
                <path d="M4 10L10 2L16 10" stroke="rgba(87,215,226,0.6)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            </div>

            {/* Tier 3 — Foundation / 7 Pillars (widest / bottom) */}
            <div style={{
              width: "100%",
              background: "linear-gradient(180deg, rgba(87,215,226,0.07) 0%, rgba(87,215,226,0.02) 100%)",
              border: "1px solid rgba(87,215,226,0.18)",
              borderRadius: "12px",
              padding: isMobile ? "20px 16px" : "22px 24px",
              textAlign: "center" as const,
              boxSizing: "border-box" as const,
            }}>
              <div style={{ color: "white", fontSize: isMobile ? "15px" : "17px", fontWeight: 700, fontFamily: ff, lineHeight: 1.2, marginBottom: "12px" }}>7 Integrated Pillars</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "center" }}>
                {["Growth Strategy", "Technical Foundations", "Content Engine", "Experience & CRO", "Web Dev", "Authority Building", "Measurement"].map(p => (
                  <span key={p} style={{
                    padding: "5px 12px",
                    borderRadius: "88px",
                    border: "1px solid rgba(87,215,226,0.3)",
                    background: "rgba(87,215,226,0.06)",
                    color: "rgba(255,255,255,0.85)",
                    fontSize: "12px",
                    fontWeight: 600,
                    fontFamily: ff,
                    whiteSpace: "nowrap" as const,
                  }}>{p}</span>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── SERVICE LADDER — how to work with us, after the proof, before the service detail ── */}
      <ServiceLadder />

      {/* ── NOTE FROM MANAGING PARTNER ── */}
      <section className="section-grid" style={{ background: "#0c0c0c", padding: isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? "40px" : "80px", alignItems: "center" }}>
          {/* Photo col */}
          <div style={{ position: "relative", borderRadius: "20px", overflow: "hidden", aspectRatio: isMobile ? "4 / 3" : "3 / 4", maxHeight: isMobile ? "300px" : "580px", width: "100%" }}>
            <img
              src={"/images/team/anjan.webp"}
              alt="Anjan — Managing Partner, Indexed"
              loading="lazy"
              style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top", display: "block" }}
            />
            {/* subtle bottom fade */}
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "30%", background: "linear-gradient(to top, rgba(12,12,12,0.6), transparent)", pointerEvents: "none" }} />
          </div>
          {/* Text col */}
          <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            <h2 style={{ color: "white", fontSize: isMobile ? "28px" : "42px", fontWeight: 700, fontFamily: ff, margin: 0, lineHeight: 1.15, letterSpacing: "-0.02em" }}>
              A note from our<br /><span style={{ color: "#57d7e2" }}>Managing Partner.</span>
            </h2>
            <p style={{ color: "rgba(255,255,255,0.65)", fontSize: isMobile ? "16px" : "18px", fontFamily: ff, lineHeight: 1.8, margin: 0 }}>
              I believe an agency should treat its clients the way a great hotel treats its guests — every detail anticipated, nothing too much trouble, and someone senior always at the door. That's the standard I hold Indexed to. No ticket queues, no junior handoffs, no waiting three days for an answer.
            </p>
            <p style={{ color: "rgba(255,255,255,0.65)", fontSize: isMobile ? "16px" : "18px", fontFamily: ff, lineHeight: 1.8, margin: 0 }}>
              And all of that service exists for one purpose: growing your revenue. We treat your budget like our own money and your pipeline like our own company's. If working with us isn't the best agency experience you've ever had — and the most profitable — we haven't done our job.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: "8px" }}>
              <span style={{ color: "white", fontSize: "17px", fontWeight: 700, fontFamily: ff }}>Anjan</span>
              <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "14px", fontFamily: ff }}>Founder &amp; Managing Partner, Indexed</span>
            </div>
            <a
              href="/about"
              style={{ color: "#57d7e2", fontSize: "15px", fontFamily: ff, fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" }}
              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = "0.7"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = "1"; }}
            >
              More about how we work →
            </a>
          </div>
        </div>
      </section>

      {/* ── WHAT TO EXPECT — split layout: sticky heading left, numbered rail right ── */}
      <section className="section-grid" style={{ background: "#0a0a0a", padding: isMobile ? "60px 20px" : "100px 72px", position: "relative", overflow: "hidden" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "0.9fr 1.2fr", gap: isMobile ? "48px" : "96px", alignItems: "start" }}>
          {/* Left — sticky heading */}
          <div style={{ position: isMobile ? "static" : "sticky", top: "120px" }}>
            <h2 style={{ color: "white", fontSize: isMobile ? "32px" : "48px", fontWeight: 700, fontFamily: ff, margin: "0 0 24px", lineHeight: 1.1, letterSpacing: "-0.02em", textAlign: "left" as const }}>
              What to expect when you<br /><span style={{ color: "#57d7e2" }}>work with Indexed.</span>
            </h2>
            <p style={{ color: "rgba(255,255,255,0.55)", fontSize: isMobile ? "16px" : "17px", fontFamily: ff, lineHeight: 1.7, margin: 0, maxWidth: "380px" }}>
              No handoffs, no silos, no resets. Three principles shape every engagement we run.
            </p>
          </div>
          {/* Right — numbered vertical rail */}
          <div style={{ position: "relative" }}>
            {/* Rail line */}
            <div style={{ position: "absolute", left: "23px", top: "24px", bottom: "24px", width: "1px", background: "linear-gradient(180deg, rgba(87,215,226,0.5) 0%, rgba(87,215,226,0.15) 100%)" }} />
            <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? "48px" : "64px" }}>
              {[
                {
                  label: "Ownership",
                  title: "Senior partners, no junior handoffs",
                  bullets: ["One partner across strategy, delivery, and optimization", "The strategist you meet is the one doing the work", "No bait-and-switch, no account managers relaying messages"],
                },
                {
                  label: "Systems thinking",
                  title: "Built for real-world complexity",
                  bullets: ["SEO, content, dev, and AI discovery all integrated", "Nothing falls through the cracks", "Scales cleanly as you grow"],
                },
                {
                  label: "Compounding value",
                  title: "Outcomes that compound, not reset",
                  bullets: ["Durable systems, not one-off projects", "Reduced reliance on short-term tactics", "Continuous optimization over time"],
                },
              ].map((item, i) => (
                <div key={i} style={{ position: "relative", paddingLeft: "72px" }}>
                  {/* Node */}
                  <div style={{
                    position: "absolute", left: 0, top: 0,
                    width: "48px", height: "48px", borderRadius: "50%",
                    background: "#0a0a0a",
                    border: "1px solid rgba(87,215,226,0.5)",
                    boxShadow: "0 0 18px rgba(87,215,226,0.15)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <span style={{ color: "#57d7e2", fontSize: "16px", fontWeight: 700, fontFamily: ff }}>0{i + 1}</span>
                  </div>
                  <div style={{ color: "#57d7e2", fontSize: "12px", fontWeight: 600, fontFamily: ff, letterSpacing: "0.1em", textTransform: "uppercase" as const, marginBottom: "8px", paddingTop: "4px" }}>{item.label}</div>
                  <h3 style={{ color: "white", fontSize: isMobile ? "20px" : "24px", fontWeight: 700, fontFamily: ff, margin: "0 0 16px", lineHeight: 1.25 }}>{item.title}</h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    {item.bullets.map((bullet, j) => (
                      <div key={j} style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                        <svg width="18" height="18" viewBox="0 0 28 28" fill="none" style={{ flexShrink: 0, marginTop: "3px" }}>
                          <circle cx="14" cy="14" r="11.67" stroke="#57d7e2" strokeWidth="1.5"/>
                          <path d="M9.5 14.5L12.5 17.5L18.5 11" stroke="#57d7e2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "15px", fontFamily: ff, margin: 0, lineHeight: 1.6 }}>{bullet}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── SERVICES (Digital Foundations) ── */}
      <section className="section-grid" style={{ background: "#0a0a0a", padding: isMobile ? "60px 20px" : "100px 72px", position: "relative", overflow: "hidden" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "24px", marginBottom: "56px" }}>
            <h2 style={{ color: "white", fontSize: isMobile ? "32px" : "48px", fontWeight: 700, fontFamily: ff, margin: 0, lineHeight: 1.1, letterSpacing: "-0.02em", textAlign: "left" as const }}>
              SEO, AI Search &amp;<br /><span style={{ color: "#57d7e2" }}>Digital Foundations.</span>
            </h2>
            <a href="/services"
              style={{ color: "#57d7e2", fontSize: "16px", fontWeight: 600, fontFamily: ff, textDecoration: "none", transition: "opacity 0.2s" }}
              onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.opacity = "0.7"}
              onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.opacity = "1"}>
              See all →
            </a>
          </div>
          {/* Editorial index rows — full-width, numbered, divider lines */}
          <div>
            {[
              { tag: "SEO & Organic Growth", title: "Search & Organic Growth Systems", desc: "We design & execute SEO programs built to compound over time — so you stop renting traffic and start owning demand.", href: "/seo-agency" },
              { tag: "AEO, GEO & AI Search", title: "AI Search & Generative Visibility", desc: "Google isn't the only place your buyers find you anymore. We get you cited in ChatGPT, Gemini and AI Overviews — visible wherever discovery happens.", href: "/ai-seo" },
              { tag: "Web Development", title: "Scalable Websites & Core Infrastructure", desc: "Your site is either helping or hurting your rankings. We build sites that search engines reward so your content gets found.", href: "/web-development" },
              { tag: "Web Design", title: "Conversion-Focused Web Design", desc: "Traffic without conversion is waste. We design sites that don't just look good, they turn visitors into pipeline.", href: "/web-design" },
            ].map((card, idx, arr) => (
              <a
                key={card.tag}
                href={card.href}
                onMouseEnter={() => setHovSvcCard(idx)}
                onMouseLeave={() => setHovSvcCard(null)}
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "64px 1.1fr 1.3fr 56px",
                  alignItems: "center",
                  gap: isMobile ? "10px" : "32px",
                  padding: isMobile ? "28px 4px" : "40px 12px",
                  borderTop: "1px solid rgba(255,255,255,0.08)",
                  borderBottom: idx === arr.length - 1 ? "1px solid rgba(255,255,255,0.08)" : "none",
                  textDecoration: "none",
                  background: hovSvcCard === idx ? "linear-gradient(90deg, rgba(87,215,226,0.06) 0%, rgba(87,215,226,0) 70%)" : "transparent",
                  transition: "background 0.25s",
                }}
              >
                <span style={{ color: hovSvcCard === idx ? "#57d7e2" : "rgba(255,255,255,0.25)", fontSize: isMobile ? "14px" : "16px", fontWeight: 600, fontFamily: ff, letterSpacing: "0.08em", transition: "color 0.25s" }}>
                  0{idx + 1}
                </span>
                <div>
                  <div style={{ color: "#57d7e2", fontSize: "12px", fontWeight: 600, fontFamily: ff, letterSpacing: "0.1em", textTransform: "uppercase" as const, marginBottom: "8px" }}>{card.tag}</div>
                  <h3 style={{ color: "white", fontSize: isMobile ? "22px" : "28px", fontWeight: 700, fontFamily: ff, margin: 0, lineHeight: 1.2 }}>{card.title}</h3>
                </div>
                <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "15px", fontFamily: ff, lineHeight: 1.65, margin: 0 }}>{card.desc}</p>
                {!isMobile && (
                  <div style={{
                    width: "48px", height: "48px", borderRadius: "50%", justifySelf: "end",
                    border: `1px solid ${hovSvcCard === idx ? "#57d7e2" : "rgba(255,255,255,0.15)"}`,
                    background: hovSvcCard === idx ? "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)" : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: hovSvcCard === idx ? "#0a0c0c" : "rgba(255,255,255,0.6)",
                    fontSize: "18px", transition: "background 0.25s, border-color 0.25s, color 0.25s, transform 0.25s",
                    transform: hovSvcCard === idx ? "rotate(-45deg)" : "rotate(0deg)",
                  }}>→</div>
                )}
              </a>
            ))}
          </div>


        </div>
      </section>

      {/* ── BLOG ARTICLES ── */}
      <HomeBlogSection isMobile={isMobile} />
            {/* ── FAQ ── */}
      <section className="section-grid" style={{ background: "#0a0a0a", padding: isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth: "860px", margin: "0 auto" }}>
          <h2 style={{ color: "white", fontSize: isMobile ? "32px" : "48px", fontWeight: 700, fontFamily: ff, lineHeight: 1.1, letterSpacing: "-0.02em", margin: "0 0 56px", textAlign: "center" as const, width: "100%" }}>
            Common questions<br /><span style={{ color: "#57d7e2" }}>before getting started.</span>
          </h2>
          <div>
            {([
              { q: "How do I know what my site is losing in revenue?", a: "That's exactly what our free video review is designed to answer. In a 10–15 minute video recorded for you, we identify the specific issues costing you the most traffic and revenue — and give you a clear picture of what fixing them is worth. No pitch, no obligation." },
              { q: "Do you sell standalone SEO services or packages?", a: "We don't sell one-off audits or isolated tactics. Everything we do is part of the Indexed Full-Stack Search Method™ — a complete, end-to-end engagement covering technical SEO, content, links, tracking, and design. This is why our clients see 3.5× ROI on average." },
              { q: "We've had SEO agencies before and seen no results. Why is Indexed different?", a: "Most agencies deliver reports. We deliver revenue. Every engagement is tracked against pipeline and closed revenue — not just rankings or traffic. We also implement fixes directly rather than handing you a document and walking away." },
              { q: "How long before we see results?", a: "Most clients see measurable improvements in organic traffic within 3–4 months. Revenue impact typically follows at 6–8 months as rankings compound. Our solar client hit $1.1m in revenue within 10 months." },
              { q: "Do you work with businesses outside of B2B SaaS?", a: "Yes — we work across sectors including renewable energy, professional services, fintech, biotech, storage, and e-commerce. The Full-Stack Search Method™ is designed to drive revenue regardless of industry." },
              { q: "How many new clients do you take on?", a: "We take on 3–4 new clients per month to maintain quality and senior attention on every engagement. If you're considering working with us, booking a session early is the best way to check availability." },
            ] as { q: string; a: string }[]).map((faq, i, arr) => (
              <HomeFaqItem key={i} faq={faq} index={i} isLast={i === arr.length - 1} />
            ))}
          </div>
        </div>
      </section>


      {/* ── FREE REVENUE GAP ANALYSIS CTA ── */}
      <section className="section-grid" style={{ background: "#080a0a", padding: isMobile ? "60px 20px" : "100px 72px", position: "relative", overflow: "hidden" }}>
        <div style={{ maxWidth: "760px", margin: "0 auto", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: "32px" }}>
          {/* Heading */}
          <h2 style={{ color: "white", fontSize: isMobile ? "28px" : "48px", fontWeight: 700, fontFamily: ff, margin: 0, lineHeight: 1.15, letterSpacing: "-0.02em" }}>
            Find out what your site is losing.<br />{" "}<span style={{ color: "#57d7e2" }}>It's free.</span>
          </h2>
          {/* Body */}
          <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "18px", fontFamily: ff, lineHeight: 1.7, margin: 0, maxWidth: "520px" }}>
            In a free personalized video review, a top-1% SEO records a walkthrough of your site and shows you exactly what's holding your rankings back — and what fixing it is worth. No pitch. No obligation.
          </p>
          {/* CTA */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "14px" }}>
            <a
              href={STRATEGIST_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: isMobile ? "14px 28px" : "20px 48px",
                background: "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)",
                borderRadius: "88px",
                border: "1px solid #57d7e2",
                boxShadow: "0px 4px 32px rgba(87,215,226,0.30)",
                textDecoration: "none",
                color: "#0a0c0c",
                fontSize: isMobile ? "15px" : "17px",
                fontWeight: 700,
                fontFamily: ff,
                letterSpacing: "-0.01em",
                transition: "opacity 0.2s, transform 0.2s",
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = "0.88"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = "1"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)"; }}
            >
              Get my free video review →
            </a>
            <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "13px", fontFamily: ff }}>
              No call &middot; No pitch &middot; We take on 3–4 new clients per month — request yours to check availability
            </span>
          </div>
          {/* Stat strip */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", marginTop: "16px", borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: "32px", width: "100%", gap: "0" }}>
            {[
              { metric: "5.0 ★", label: "from 100+ reviews" },
              { metric: "100+", label: "engagements delivered" },
              { metric: "Avg. 3.5×", label: "return on SEO investment" },
            ].map((s, i) => (
              <div key={s.metric} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "6px", padding: isMobile ? "12px 8px" : "12px 16px", borderLeft: i > 0 ? "1px solid rgba(255,255,255,0.1)" : "none", textAlign: "center" as const }}>
                <span style={{ color: "#57d7e2", fontSize: isMobile ? "18px" : "22px", fontWeight: 700, fontFamily: ff, lineHeight: 1 }}>{s.metric}</span>
                <span style={{ color: "rgba(255,255,255,0.45)", fontSize: isMobile ? "11px" : "13px", fontFamily: ff, lineHeight: 1.3 }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ── GLOBAL STYLES ── */}
      <style>{`
        html { scroll-behavior: smooth; }
        @keyframes scroll-logos {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        * { box-sizing: border-box; }
        body { margin: 0; padding: 0; }
      `}</style>
    </SiteLayout>
  );
}
